import { Component } from 'preact';
import { onSessionSuperseded, reclaimSession, isSessionSuperseded, getSupersededInfo } from '../utils/serverClient';
import { isWeb } from '../utils/platform';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, ACCENT_GOLD, DIALOG_STYLE, BEVELED_BTN, FourCorners,
} from '../lib/medievalTheme';
import { t } from '../localization/t';

function quitApp() {
  fetch('/api/app/quit', { method: 'POST' }).catch(() => {});
}

const OVERLAY_BTN_STYLE = {
  ...BEVELED_BTN,
  padding: '0.7cqw 1.4cqw',
  fontSize: '0.78cqw',
  cursor: 'pointer',
};

// Where did the account go? Names the other client so the player who
// returns to this abandoned tab / window understands what happened.
function placeLabel(info) {
  if (info?.platform === 'web') return t('in a browser tab');
  if (info?.platform === 'desktop') return t('in the desktop app');
  return t('on another device or tab');
}

// Shown when the server retired this connection because the same account
// connected somewhere else (session:superseded / close 4002). Unlike the
// connection-lost overlay this one never auto-resolves: the reconnect
// loop is deliberately latched off, so the message stays up until the
// player either reclaims the session here or walks away. That
// persistence is the point. Whenever they return to this tab or client,
// it tells them why it went quiet.
export default class SessionSupersededOverlay extends Component {
  state = {
    visible: isSessionSuperseded(),
    info: getSupersededInfo(),
    reclaiming: false,
  };

  componentDidMount() {
    this._unsub = onSessionSuperseded((info) => {
      this.setState({ visible: true, info, reclaiming: false });
    });
  }

  componentWillUnmount() {
    if (this._unsub) this._unsub();
  }

  handleReclaim = async () => {
    if (this.state.reclaiming) return;
    this.setState({ reclaiming: true });
    const connected = await reclaimSession();
    if (connected) {
      this.setState({ visible: false, reclaiming: false });
    } else {
      // Couldn't get back in (server unreachable, or superseded again
      // before the open). Stay up so the player can retry.
      this.setState({ reclaiming: false });
    }
  };

  render() {
    if (!this.state.visible) return null;
    const { info, reclaiming } = this.state;
    return (
      <div
        className="fixed inset-0 z-[360] flex items-center justify-center"
        style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
      >
        <div className="relative w-full max-w-[30cqw] p-7 text-center" style={DIALOG_STYLE}>
          <FourCorners />
          <div
            className="text-[0.625cqw] font-semibold mb-3"
            style={{ color: `${GOLD} 0.6)` }}
          >
            {t('Signed In Elsewhere')}
          </div>
          <h2
            className="text-xl font-bold arena-heading mb-4"
            style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
          >
            {t('Your Account Is Playing Somewhere Else')}
          </h2>
          <p className="text-sm leading-relaxed mb-5" style={{ color: TEXT_BODY }}>
            {t('Your account just signed in {place}, so this window has been disconnected. Valkenhall can only be connected from one place at a time.', { place: placeLabel(info) })}
          </p>
          <div className="flex justify-center gap-3">
            <button
              type="button"
              className="arena-heading font-semibold"
              style={{ ...OVERLAY_BTN_STYLE, color: ACCENT_GOLD, opacity: reclaiming ? 0.6 : 1 }}
              disabled={reclaiming}
              onClick={this.handleReclaim}
            >
              {reclaiming ? t('Reconnecting…') : t('Play Here Instead')}
            </button>
            {isWeb() ? null : (
              <button
                type="button"
                className="arena-heading font-semibold"
                style={{ ...OVERLAY_BTN_STYLE, color: TEXT_BODY }}
                onClick={quitApp}
              >
                {t('Quit')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
